import type { InputHTMLAttributes, KeyboardEvent } from 'react'
import { SHORTHAND_SUFFIXES } from '@/lib/format'

/**
 * What a number field takes (John, Nov 3 2026: letters were getting into the swap amount):
 * decimal = digits and one decimal point (amounts, prices, percentages);
 * integer = digits only (order counts, intervals);
 * shorthand = a dollar figure the way parseUsdShorthand reads it ("2.5m", "600k", "$2,480,000").
 */
export type NumericMode = 'decimal' | 'integer' | 'shorthand'

/** The text a number field keeps from what was typed or pasted; everything else is dropped. Cleaning twice changes nothing. */
export function cleanNumeric(raw: string, mode: NumericMode): string {
  if (mode === 'integer') return raw.replace(/\D/g, '')
  if (mode === 'shorthand') {
    // an optional $, the figure with . or , separators, then only letters that can still become a scale suffix (k, m, bn, million…)
    const match = /^(\$?)([\d.,]*)([a-zA-Z]*)/.exec(raw.replace(/[^\d.,$a-zA-Z]/g, ''))
    if (!match) return ''
    let letters = match[3]
    while (letters && !SHORTHAND_SUFFIXES.some((suffix) => suffix.startsWith(letters.toLowerCase()))) letters = letters.slice(0, -1)
    return match[1] + onePoint(match[2]) + letters
  }
  // commas only arrive by paste, as thousands separators ("1,000.5")
  return onePoint(raw.replace(/[^\d.]/g, ''))
}

/** keeps the first decimal point and drops any later one */
function onePoint(text: string): string {
  const dot = text.indexOf('.')
  return dot === -1 ? text : text.slice(0, dot + 1) + text.slice(dot + 1).replace(/\./g, '')
}

type Props = Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'inputMode' | 'value' | 'onChange'> & {
  mode?: NumericMode
  value: string
  /** receives the cleaned text, so a letter never reaches state */
  onChange: (value: string) => void
}

/** A text input that only takes a number: a key that would break the figure is refused as it is typed, and pasted text is cleaned. */
export function NumericInput({ mode = 'decimal', value, onChange, onKeyDown, ...rest }: Props) {
  function refuseNonNumeric(event: KeyboardEvent<HTMLInputElement>) {
    onKeyDown?.(event)
    if (event.defaultPrevented || event.key.length !== 1 || event.ctrlKey || event.metaKey || event.altKey) return
    const input = event.currentTarget
    const start = input.selectionStart ?? input.value.length
    const end = input.selectionEnd ?? start
    const next = input.value.slice(0, start) + event.key + input.value.slice(end)
    if (cleanNumeric(next, mode) !== next) event.preventDefault()
  }
  return (
    <input
      type="text"
      inputMode={mode === 'integer' ? 'numeric' : 'decimal'}
      autoComplete="off"
      autoCapitalize="off"
      autoCorrect="off"
      spellCheck={false}
      {...rest}
      value={value}
      onKeyDown={refuseNonNumeric}
      onChange={(event) => onChange(cleanNumeric(event.target.value, mode))}
    />
  )
}
